import React from "react";
import { Helmet } from "react-helmet-async";

const SITE = {
  name: "Perforange",
  url: "https://perforange.com",
  defaultTitle: "Perforange · Built for schools that take student development seriously",
  defaultDescription:
    "India has the world's largest gaming generation — and no structured path to turn that talent into careers.",
  defaultImage: "https://perforange.com/og-default.jpg",
  twitter: "@perforange",
};

function absUrl(maybe) {
  if (!maybe) return null;
  if (/^https?:\/\//i.test(maybe)) return maybe;
  const base = (process.env.REACT_APP_BACKEND_URL || SITE.url).replace(/\/$/, "");
  const path = maybe.startsWith("/") ? maybe : `/${maybe}`;
  return `${base}${path}`;
}

/**
 * Reusable SEO / Open Graph component.
 *
 * Props:
 *   - title:        Page-specific title (falls back to default site title).
 *   - description:  Meta description / OG description.
 *   - image:        Absolute or relative path to OG image. Will be normalized to https://.
 *   - url:          Canonical URL (auto-derived from window.location if omitted on client).
 *   - type:         OG type — "article" for blog posts, otherwise "website".
 *   - article:      Optional metadata for articles { published_time, modified_time, author, section, tags[] }.
 */
export default function Seo({
  title,
  description,
  image,
  url,
  type = "website",
  article,
}) {
  const t = title ? `${title} · ${SITE.name}` : SITE.defaultTitle;
  const d = description || SITE.defaultDescription;
  const img = absUrl(image) || SITE.defaultImage;
  const canonical =
    url ||
    (typeof window !== "undefined" ? `${SITE.url}${window.location.pathname}` : SITE.url);

  return (
    <Helmet prioritizeSeoTags>
      <title>{t}</title>
      <meta name="description" content={d} />
      <link rel="canonical" href={canonical} />

      {/* Open Graph */}
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content={SITE.name} />
      <meta property="og:title" content={t} />
      <meta property="og:description" content={d} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={img} />
      <meta property="og:image:secure_url" content={img} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content={t} />
      <meta property="og:locale" content="en_IN" />

      {/* Article-specific */}
      {type === "article" && article?.published_time && (
        <meta property="article:published_time" content={article.published_time} />
      )}
      {type === "article" && article?.modified_time && (
        <meta property="article:modified_time" content={article.modified_time} />
      )}
      {type === "article" && article?.author && (
        <meta property="article:author" content={article.author} />
      )}
      {type === "article" && article?.section && (
        <meta property="article:section" content={article.section} />
      )}
      {type === "article" &&
        Array.isArray(article?.tags) &&
        article.tags.map((tag, i) => (
          <meta key={`tag-${i}`} property="article:tag" content={tag} />
        ))}

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={t} />
      <meta name="twitter:description" content={d} />
      <meta name="twitter:image" content={img} />
      <meta name="twitter:image:alt" content={t} />
      <meta name="twitter:site" content={SITE.twitter} />

      {/* LinkedIn (uses OG tags primarily — these are belt + suspenders) */}
      <meta name="author" content={article?.author || "Perforange"} />
    </Helmet>
  );
}
